import type { DispatchFreightDraft } from '../../dispatch-freight/models/dispatch-freight.models';
import type { IPalletProductPayload } from '../freightmancerlvl99.models';

const CENTIMETERS_PER_INCH = 2.54;
const POUNDS_PER_KILOGRAM = 2.2046226218;

export class DispatchFreightLegacyPayloadError extends Error {
  readonly lineIds: string[];

  constructor(lineIds: string[], message: string) {
    super(message);
    this.name = 'DispatchFreightLegacyPayloadError';
    this.lineIds = lineIds;
  }
}

/**
 * Produces the pallet/product array consumed by freightmancers_grimoire.php.
 * The legacy writer has no unit fields, so manual dimensions are normalized to
 * inches and weights to pounds before crossing that boundary.
 * This is only the existing HTTP compatibility shape. The active writer
 * persists packages through v_packages, v_lading_packages, and v_products.
 *
 * Catalog lines intentionally fail closed: a UUID alone does not contain the
 * dimensions/weight the legacy writer needs to create v_packages/v_products.
 */
export function dispatchFreightToLegacyPalletProducts(
  draft: DispatchFreightDraft,
): IPalletProductPayload[] {
  const catalogLineIds = draft.items
    .filter((line) => line.source === 'catalog')
    .map((line) => line.id);

  if (catalogLineIds.length > 0) {
    throw new DispatchFreightLegacyPayloadError(
      catalogLineIds,
      'Catalog freight must be resolved to an authoritative product snapshot before lading creation.',
    );
  }

  return draft.items.map((line) => {
    if (line.source !== 'manual') {
      throw new DispatchFreightLegacyPayloadError([line.id], 'Unsupported dispatch freight line.');
    }

    return {
      mode: 'product',
      pallet_num: null,
      pallet_length: null,
      pallet_width: null,
      pallet_height: null,
      pallet_weight: null,
      product_model_number: null,
      product_quantity: requirePositiveWholeNumber(line.package_quantity, line.id, 'quantity'),
      product_length: normalizeDimension(line.length, line.measurement_unit, line.id, 'length'),
      product_width: normalizeDimension(line.width, line.measurement_unit, line.id, 'width'),
      product_height: normalizeDimension(line.height, line.measurement_unit, line.id, 'height'),
      product_weight: line.weight === null
        ? null
        : normalizeWeight(line.weight, line.weight_unit, line.id),
      product_description: cleanString(line.label) || null,
    };
  });
}

function normalizeDimension(
  value: number | null,
  unit: 'IN' | 'CM',
  lineId: string,
  field: string,
): number {
  const positive = requirePositiveNumber(value, lineId, field);
  return round(unit === 'CM' ? positive / CENTIMETERS_PER_INCH : positive);
}

function normalizeWeight(value: number, unit: 'LB' | 'KG', lineId: string): number {
  const positive = requirePositiveNumber(value, lineId, 'weight');
  return round(unit === 'KG' ? positive * POUNDS_PER_KILOGRAM : positive);
}

function requirePositiveWholeNumber(value: number | null, lineId: string, field: string): number {
  const positive = requirePositiveNumber(value, lineId, field);
  if (!Number.isInteger(positive)) {
    throw new DispatchFreightLegacyPayloadError(
      [lineId],
      `Freight line ${lineId} ${field} must be a whole number.`,
    );
  }
  return positive;
}

function requirePositiveNumber(value: number | null, lineId: string, field: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    throw new DispatchFreightLegacyPayloadError(
      [lineId],
      `Freight line ${lineId} ${field} must be greater than zero.`,
    );
  }
  return value;
}

function round(value: number): number {
  return Math.round(value * 1_000_000) / 1_000_000;
}

function cleanString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}
